import React, { useState, useCallback } from "react";
import { DetailsPermissionsContainer, ContentWrapper, LeftColumn, RightColumn, RightColumnContent, BreadcrumbContainer, TwoColumnContainer, FirstColumn, SecondColumn, SwitchLabelContainer, SwitchTitleContainer } from "./DetailsPermissions.styled";
import { Header, ExpandablePermissionCard } from "./components";
import { Card, Switch, Typography, ButtonMenu } from "@ann/web-components";
import DynamicBreadcrumbs from "../../../../../elements/v2/DynamicBreadcrumbs/DynamicBreadcrumbs";

const DetailsPermissions = () => {
  const [expandedCards, setExpandedCards] = useState({
    agents: false,
    "agents-edit": false,
    "agents-duplicate": false,
    automation: false,
    "automation-create": false,
    "automation-edit": false,
    data: false,
    "data-export": false,
    "data-import": false,
    "data-reports": false,
    drives: false,
    "drives-upload": false,
    "drives-download": false,
    marketing: false,
    "marketing-create": false,
    "marketing-edit": false,
    "marketing-analytics": false,
    "right-edit": false,
    "right-create": false,
    "right-view": false,
    "right-share": false,
  });

  const handleToggleCard = useCallback((cardId) => {
    setExpandedCards((prev) => ({
      ...prev,
      [cardId]: !prev[cardId],
    }));
  }, []);

  const handleSwitchChange = useCallback((cardId) => (checked) => {
    console.log(`${cardId} switch changed:`, checked);
  }, []);

  return (
    <DetailsPermissionsContainer>
      <Header />
      <ContentWrapper>
        <LeftColumn>
          <Card size="medium">
            <div style={{ padding: "8px", display: "flex", flexDirection: "column", gap: "8px" }}>
              <ExpandablePermissionCard
                title="Agents"
                subtitle="4 permissions · 3 active, 1 disabled"
                icon="person"
                showIcon={true}
                onChange={handleSwitchChange("agents")}
                label="Agents"
                tagValue="4"
                isExpanded={expandedCards.agents}
                onToggle={() => handleToggleCard("agents")}
              >
                <ExpandablePermissionCard
                  title="Edit Knowledge"
                  subtitle="Modify agent responses and learning content"
                  showIcon={false}
                  onChange={handleSwitchChange("agents-edit")}
                  label="Edit Knowledge"
                  tagValue="1"
                  isExpanded={expandedCards["agents-edit"]}
                  onToggle={() => handleToggleCard("agents-edit")}
                  noBorder={true}
                />
                <ExpandablePermissionCard
                  title="Duplicate Agents"
                  subtitle="Create copies of existing agents"
                  showIcon={false}
                  onChange={handleSwitchChange("agents-duplicate")}
                  label="Duplicate Agents"
                  tagValue="1"
                  isExpanded={expandedCards["agents-duplicate"]}
                  onToggle={() => handleToggleCard("agents-duplicate")}
                  noBorder={true}
                />
              </ExpandablePermissionCard>
              <ExpandablePermissionCard
                title="Automation"
                subtitle="3 permissions · 3 active"
                icon="settings"
                showIcon={true}
                onChange={handleSwitchChange("automation")}
                label="Automation"
                tagValue="3"
                isExpanded={expandedCards.automation}
                onToggle={() => handleToggleCard("automation")}
              >
                <ExpandablePermissionCard
                  title="Create Workflows"
                  subtitle="Build automated workflows for tasks"
                  showIcon={false}
                  onChange={handleSwitchChange("automation-create")}
                  label="Create Workflows"
                  tagValue="1"
                  isExpanded={expandedCards["automation-create"]}
                  onToggle={() => handleToggleCard("automation-create")}
                  noBorder={true}
                />
                <ExpandablePermissionCard
                  title="Edit Workflows"
                  subtitle="Modify existing automation rules"
                  showIcon={false}
                  onChange={handleSwitchChange("automation-edit")}
                  label="Edit Workflows"
                  tagValue="1"
                  isExpanded={expandedCards["automation-edit"]}
                  onToggle={() => handleToggleCard("automation-edit")}
                  noBorder={true}
                />
              </ExpandablePermissionCard>
              <ExpandablePermissionCard
                title="Data"
                subtitle="3 permissions · 3 active, 1 disabled"
                icon="description"
                showIcon={true}
                onChange={handleSwitchChange("data")}
                label="Data"
                tagValue="3"
                isExpanded={expandedCards.data}
                onToggle={() => handleToggleCard("data")}
              >
                <ExpandablePermissionCard
                  title="Export Data"
                  subtitle="Download data in various formats"
                  showIcon={false}
                  onChange={handleSwitchChange("data-export")}
                  label="Export Data"
                  tagValue="1"
                  isExpanded={expandedCards["data-export"]}
                  onToggle={() => handleToggleCard("data-export")}
                  noBorder={true}
                />
                <ExpandablePermissionCard
                  title="Import Data"
                  subtitle="Upload and process external data files"
                  showIcon={false}
                  onChange={handleSwitchChange("data-import")}
                  label="Import Data"
                  tagValue="1"
                  isExpanded={expandedCards["data-import"]}
                  onToggle={() => handleToggleCard("data-import")}
                  noBorder={true}
                />
                <ExpandablePermissionCard
                  title="View Reports"
                  subtitle="Access analytics and reporting dashboards"
                  showIcon={false}
                  onChange={handleSwitchChange("data-reports")}
                  label="View Reports"
                  tagValue="1"
                  isExpanded={expandedCards["data-reports"]}
                  onToggle={() => handleToggleCard("data-reports")}
                  noBorder={true}
                />
              </ExpandablePermissionCard>
              <ExpandablePermissionCard
                title="Drives"
                subtitle="4 permissions · 3 active, 1 disabled"
                icon="folder"
                showIcon={true}
                onChange={handleSwitchChange("drives")}
                label="Drives"
                tagValue="4"
                isExpanded={expandedCards.drives}
                onToggle={() => handleToggleCard("drives")}
              >
                <ExpandablePermissionCard
                  title="Upload Files"
                  subtitle="Add new files to shared drives"
                  showIcon={false}
                  onChange={handleSwitchChange("drives-upload")}
                  label="Upload Files"
                  tagValue="1"
                  isExpanded={expandedCards["drives-upload"]}
                  onToggle={() => handleToggleCard("drives-upload")}
                  noBorder={true}
                />
                <ExpandablePermissionCard
                  title="Download Files"
                  subtitle="Save files from drives to local storage"
                  showIcon={false}
                  onChange={handleSwitchChange("drives-download")}
                  label="Download Files"
                  tagValue="1"
                  isExpanded={expandedCards["drives-download"]}
                  onToggle={() => handleToggleCard("drives-download")}
                  noBorder={true}
                />
              </ExpandablePermissionCard>
              <ExpandablePermissionCard
                title="Marketing"
                subtitle="4 permissions · 3 active, 1 disabled"
                icon="chat"
                showIcon={true}
                onChange={handleSwitchChange("marketing")}
                label="Marketing"
                tagValue="4"
                isExpanded={expandedCards.marketing}
                onToggle={() => handleToggleCard("marketing")}
              >
                <ExpandablePermissionCard
                  title="Create Campaigns"
                  subtitle="Design and launch marketing campaigns"
                  showIcon={false}
                  onChange={handleSwitchChange("marketing-create")}
                  label="Create Campaigns"
                  tagValue="1"
                  isExpanded={expandedCards["marketing-create"]}
                  onToggle={() => handleToggleCard("marketing-create")}
                  noBorder={true}
                />
                <ExpandablePermissionCard
                  title="Edit Campaigns"
                  subtitle="Modify active or draft campaigns"
                  showIcon={false}
                  onChange={handleSwitchChange("marketing-edit")}
                  label="Edit Campaigns"
                  tagValue="1"
                  isExpanded={expandedCards["marketing-edit"]}
                  onToggle={() => handleToggleCard("marketing-edit")}
                  noBorder={true}
                />
                <ExpandablePermissionCard
                  title="View Analytics"
                  subtitle="Access campaign performance metrics"
                  showIcon={false}
                  onChange={handleSwitchChange("marketing-analytics")}
                  label="View Analytics"
                  tagValue="1"
                  isExpanded={expandedCards["marketing-analytics"]}
                  onToggle={() => handleToggleCard("marketing-analytics")}
                  noBorder={true}
                />
              </ExpandablePermissionCard>
            </div>
          </Card>
        </LeftColumn>
        <RightColumn>
          <Card size="medium">
            <RightColumnContent>
              <BreadcrumbContainer>
                <DynamicBreadcrumbs
                  items={[
                    { label: "Agents", href: "#" },
                    { label: "Use Agents", href: "#" },
                  ]}
                  currentPath={window.location.pathname}
                />
              </BreadcrumbContainer>
              <TwoColumnContainer>
                <FirstColumn>
                  <SwitchLabelContainer>
                    <SwitchTitleContainer>
                      <Switch onChange={() => {}} size="small" />
                      <Typography category="body" size="small" weight="bold">
                        Use Agents
                      </Typography>
                    </SwitchTitleContainer>
                    <Typography category="body" size="small" weight="regular" color="body">
                      Son descriptions, this description don't need to be long max two lines of text.
                    </Typography>
                  </SwitchLabelContainer>
                </FirstColumn>
                <SecondColumn>
                  <ButtonMenu
                    iconPosition="right"
                    label="Rooftop"
                    menuPosition="bottom"
                    onChange={() => {}}
                    size="medium"
                    type="secondary"
                    width="auto"
                  >
                    <ButtonMenu.Menu>
                      <ButtonMenu.Item
                        label="Rooftop"
                        value="rooftop"
                      >
                        Rooftop
                      </ButtonMenu.Item>
                      <ButtonMenu.Item
                        label="Department"
                        value="department"
                      >
                        Department
                      </ButtonMenu.Item>
                      <ButtonMenu.Item
                        label="All Departments"
                        value="all_departments"
                      >
                        All Departments
                      </ButtonMenu.Item>
                      <ButtonMenu.Item
                        label="None"
                        value="none"
                      >
                        None
                      </ButtonMenu.Item>
                    </ButtonMenu.Menu>
                  </ButtonMenu>
                </SecondColumn>
              </TwoColumnContainer>
              <ExpandablePermissionCard
                title="Edit"
                subtitle="Modify and update existing content"
                showIcon={false}
                showSwitch={true}
                onChange={handleSwitchChange("right-edit")}
                label=""
                tagValue="4"
                buttonMenu={
                  <ButtonMenu
                    iconPosition="right"
                    label="Rooftop"
                    menuPosition="bottom"
                    onChange={() => {}}
                    size="medium"
                    type="secondary"
                    width="auto"
                  >
                    <ButtonMenu.Menu>
                      <ButtonMenu.Item
                        label="Rooftop"
                        value="rooftop"
                      >
                        Rooftop
                      </ButtonMenu.Item>
                      <ButtonMenu.Item
                        label="Department"
                        value="department"
                      >
                        Department
                      </ButtonMenu.Item>
                      <ButtonMenu.Item
                        label="All Departments"
                        value="all_departments"
                      >
                        All Departments
                      </ButtonMenu.Item>
                      <ButtonMenu.Item
                        label="None"
                        value="none"
                      >
                        None
                      </ButtonMenu.Item>
                    </ButtonMenu.Menu>
                  </ButtonMenu>
                }
                isExpanded={expandedCards["right-edit"]}
                onToggle={() => handleToggleCard("right-edit")}
                noBorder={true}
              />
              <ExpandablePermissionCard
                title="Create"
                subtitle="Generate new content and resources"
                showIcon={false}
                showSwitch={true}
                onChange={handleSwitchChange("right-create")}
                label=""
                tagValue="3"
                buttonMenu={
                  <ButtonMenu
                    iconPosition="right"
                    label="Rooftop"
                    menuPosition="bottom"
                    onChange={() => {}}
                    size="medium"
                    type="secondary"
                    width="auto"
                  >
                    <ButtonMenu.Menu>
                      <ButtonMenu.Item
                        label="Rooftop"
                        value="rooftop"
                      >
                        Rooftop
                      </ButtonMenu.Item>
                      <ButtonMenu.Item
                        label="Department"
                        value="department"
                      >
                        Department
                      </ButtonMenu.Item>
                      <ButtonMenu.Item
                        label="All Departments"
                        value="all_departments"
                      >
                        All Departments
                      </ButtonMenu.Item>
                      <ButtonMenu.Item
                        label="None"
                        value="none"
                      >
                        None
                      </ButtonMenu.Item>
                    </ButtonMenu.Menu>
                  </ButtonMenu>
                }
                isExpanded={expandedCards["right-create"]}
                onToggle={() => handleToggleCard("right-create")}
                noBorder={true}
              />
              <ExpandablePermissionCard
                title="View"
                subtitle="Access and read content without modifications"
                showIcon={false}
                showSwitch={true}
                onChange={handleSwitchChange("right-view")}
                label=""
                tagValue="2"
                buttonMenu={
                  <ButtonMenu
                    iconPosition="right"
                    label="Rooftop"
                    menuPosition="bottom"
                    onChange={() => {}}
                    size="medium"
                    type="secondary"
                    width="auto"
                  >
                    <ButtonMenu.Menu>
                      <ButtonMenu.Item
                        label="Rooftop"
                        value="rooftop"
                      >
                        Rooftop
                      </ButtonMenu.Item>
                      <ButtonMenu.Item
                        label="Department"
                        value="department"
                      >
                        Department
                      </ButtonMenu.Item>
                      <ButtonMenu.Item
                        label="All Departments"
                        value="all_departments"
                      >
                        All Departments
                      </ButtonMenu.Item>
                      <ButtonMenu.Item
                        label="None"
                        value="none"
                      >
                        None
                      </ButtonMenu.Item>
                    </ButtonMenu.Menu>
                  </ButtonMenu>
                }
                isExpanded={expandedCards["right-view"]}
                onToggle={() => handleToggleCard("right-view")}
                noBorder={true}
              />
              <ExpandablePermissionCard
                title="Share"
                subtitle="Distribute content with other users or teams"
                showIcon={false}
                showSwitch={true}
                onChange={handleSwitchChange("right-share")}
                label=""
                tagValue="1"
                buttonMenu={
                  <ButtonMenu
                    iconPosition="right"
                    label="Rooftop"
                    menuPosition="bottom"
                    onChange={() => {}}
                    size="medium"
                    type="secondary"
                    width="auto"
                  >
                    <ButtonMenu.Menu>
                      <ButtonMenu.Item
                        label="Rooftop"
                        value="rooftop"
                      >
                        Rooftop
                      </ButtonMenu.Item>
                      <ButtonMenu.Item
                        label="Department"
                        value="department"
                      >
                        Department
                      </ButtonMenu.Item>
                      <ButtonMenu.Item
                        label="All Departments"
                        value="all_departments"
                      >
                        All Departments
                      </ButtonMenu.Item>
                      <ButtonMenu.Item
                        label="None"
                        value="none"
                      >
                        None
                      </ButtonMenu.Item>
                    </ButtonMenu.Menu>
                  </ButtonMenu>
                }
                isExpanded={expandedCards["right-share"]}
                onToggle={() => handleToggleCard("right-share")}
                noBorder={true}
              />
            </RightColumnContent>
          </Card>
        </RightColumn>
      </ContentWrapper>
    </DetailsPermissionsContainer>
  );
};

export default DetailsPermissions;

